import { memo, useState, useEffect } from 'react';
import { Box, styled } from '@mui/material';
import { MINIMIZED_FUNNEL_PREVIEW_HEIGHT } from '../../constants';
import { usePreviewCacheOptional } from './PreviewCacheContext';

interface FunnelMapCanvasPreviewProps {
  funnelMap: FunnelMap;
  sxProps?: any;
  showPreviewBackground?: boolean;
}

const PreviewContainer = styled(Box)({
  height: MINIMIZED_FUNNEL_PREVIEW_HEIGHT,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  padding: '20px',
  boxSizing: 'border-box',
});

const PreviewImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
});

const FunnelMapPreviewSimple = ({ funnelMap, sxProps }: FunnelMapCanvasPreviewProps) => {
  const cache = usePreviewCacheOptional();
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!cache) {
      setPreviewImageUrl(null);
      return;
    }

    const url = cache.getPreviewUrl(funnelMap);
    setPreviewImageUrl(url);

    const unsubscribe = cache.subscribe(() => {
      const updatedUrl = cache.getPreviewUrl(funnelMap);
      setPreviewImageUrl(updatedUrl);
    });

    return unsubscribe;
  }, [cache, funnelMap]);

  return (
    <PreviewContainer sx={sxProps} data-testid="funnel-preview">
      {previewImageUrl && (
        <PreviewImage src={previewImageUrl} alt="Funnel map preview" draggable={false} />
      )}
    </PreviewContainer>
  );
};

const memoizedFunnelMapPreviewSimple = memo(FunnelMapPreviewSimple);

export { memoizedFunnelMapPreviewSimple as FunnelMapPreviewSimple };
